import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import _groupBy from 'lodash/groupBy';
import _get from 'lodash/get';
import RutHelper from '@/helpers/rut';

const LOCAL_STORE_PREFIX = process.env.VUE_APP_LOCAL_STORE_PREFIX;
const API_URL = process.env.VUE_APP_API_URL;
const CLAVEUNICA_LOGOUT_URL = process.env.VUE_APP_MINSAL_API_REFERENCE_URL + '/logout';

const minsalAxios = axios.create({
  baseURL: API_URL
});

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    drugForms: [],
    unitsOfTime: [],
    routeCodes: [],
    user: null,
    localToken: localStorage.getItem(`${LOCAL_STORE_PREFIX}-user-token`),
    disablePrescriptions: process.env.VUE_APP_DISABLE_PRESCRIPTIONS === 'true'
  },
  getters: {
    user(state) {
      return Object.assign({
        full_name: `${state.user.given_name} ${state.user.family_name}`
      }, state.user);
    },
    organizations(state, getters) {
      return getters.user.organizations.map(o => {
        o.cleanRut = RutHelper.cleanRut(o.rut);
        return o;
      });
    },
    hasRole: (state) => (role) => {
      return state.user.roles.find(r => r.role === role);
    },
    requesterResource(state, getters) {
      return {
        id: getters.user.claveunica_id,
        type: 'Practitioner',
        display: getters.user.full_name,
        reference: `${process.env.VUE_APP_MINSAL_API_REFERENCE_URL}/prescripcion/v1/practitioners?identifier=http://registrocivil.cl|${getters.user.claveunica_id}`,
        name: {
          given: getters.user.given_name,
          family: getters.user.family_name
        },
        address: {
          text: _get(getters.user, 'address', ''),
          state: _get(getters.user, 'state', ''),
          district: _get(getters.user, 'district', '')
        }
      };
    },
    loggedIn(state) {
      return state.user !== null;
    },
    hasSavedSession(state) {
      return state.localToken !== null;
    },
    drugForms(state) {
      return state.drugForms;
    },
    unitsOfTime(state) {
      return state.unitsOfTime;
    },
    routeCodes(state) {
      return state.routeCodes;
    }
  },
  actions: {
    async restoreUserSessionFromToken({ dispatch }, token) {
      // Set global Authorization header
      minsalAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return dispatch('getUserInfo', token)
        .then(response => {
          return dispatch('restoreUserSession', { user: response.data, token });
        })
        .catch(e => {
          dispatch('logout', e);
        });
    },
    async logout({ commit, dispatch }) {
      minsalAxios.defaults.headers.common['Authorization'] = null;
      commit('LOGOUT');
      dispatch('claveUnicaLogout');
    },
    async claveUnicaLogout() {
      window.location.href = `${CLAVEUNICA_LOGOUT_URL}?redirect=${encodeURIComponent(location.origin)}`;
    },
    async getUserInfo() {
      return minsalAxios.get(`/me`);
    },
    async fetchSubjectInformation(context, subjectId) {
      return minsalAxios.get(`/subject/${subjectId}`)
        .then(response => response.data);
    },
    async fetchSubjectInformationForeigs(context, payload){
      return minsalAxios.get(`/subject-foreigs/${payload.subjectId}/${payload.type}/${payload.country}`)
      .then(response => response.data);
    },
    async restoreUserSession({ commit }, payload) {
      commit('LOGIN', payload);
      return Promise.resolve(payload.user);
    },
    async searchMultipleMedication(context, search) {
      return minsalAxios.get(`/semantikos/search-multiple?search=${search}`)
        .then(response => {
          return {
            requestSearch: search,
            response: [
              ...response.data.BASIC,
              ...response.data.CLINIC,
              ...response.data.COMMERCIAL
            ]
          };
        });
    },
    async searchMedication(context, search) {
      return minsalAxios.get(`/semantikos?search=${search}`)
        .then(response => {
          return {
            requestSearch: search,
            response
          };
        });
    },
    async fetchMedicationDetails(context, medicationReference) {
      const medicationId = medicationReference.reference.split('/').pop();
      return minsalAxios.get(`/semantikos/${medicationId}`);
    },
    async fetchClinicalMedication({ commit }, medicationReference) {
      const medicationId = medicationReference.reference.split('/').pop();
      return minsalAxios.get(`/semantikos/${medicationId}/clinical-alternatives`);
    },
    async fetchRefsMedication({ commit }, medicationReference) {
      const medicationId = medicationReference.reference.split('/').pop();
      return minsalAxios.get(`/semantikos/${medicationId}/refs`);
    },
    async fetchCommercialAlternatives({ commit }, medicationReference) {
      const medicationId = medicationReference.reference.split('/').pop();
      return minsalAxios.get(`/semantikos/${medicationId}/commercial-alternatives`);
    },
    async fetchDispensableCommercialAlternatives(context, commercialMedicationReference) {
      const medicationId = commercialMedicationReference.reference.split('/').pop();
      return minsalAxios.get(`/semantikos/${medicationId}/dispensable-alternatives`);
    },
    async validateMedicationRequestForm(context, medicationRequest) {
      return minsalAxios.post(`/medication-requests/validate`, medicationRequest);
    },
    async fetchMedicationsMetadata({ dispatch }) {
      return Promise.all([
        dispatch('fetchMedicationDrugForms'),
        dispatch('fetchMedicationUnitsOfTime'),
        dispatch('fetchRouteCodes')
      ]);
    },
    async fetchMedicationDrugForms({ commit }) {
      return minsalAxios.get(`/fhir/drug-forms`)
        .then(response => {
          commit('FETCH_DRUG_FORMS_SUCCESS', response.data);
        });
    },
    async fetchMedicationUnitsOfTime({ commit }) {
      return minsalAxios.get(`/fhir/units-of-time`)
        .then(response => {
          commit('FETCH_UNITS_OF_TIME_SUCCESS', response.data);
        });
    },
    async fetchRouteCodes({ commit }) {
      return minsalAxios.get(`/fhir/route-codes`)
        .then(response => {
          commit('FETCH_ROUTE_CODES_SUCCESS', response.data);
        });
    },
    async fetchPrescriptions(context, { limit = 10, offset = 0, patientRut = '', boundPeriod = ['', ''], status = '' }) {
      return minsalAxios.get(`/medication-requests?limit=${limit}&offset=${offset}&patient-rut=${patientRut}&date-from=${boundPeriod[0]}&date-to=${boundPeriod[1]}&status=${status}`)
        .then(response => {
          return {
            data: _groupBy(response.data.entry.map(r => r.resource), (m) => {
              return m.groupIdentifier.value;
            }),
            meta: response.data.meta
          };
        });
    },
    async fetchPrescriptionPreview(context, prescriptionId) {
      return minsalAxios.get(`/medication-requests/${prescriptionId}/preview`);
    },
    async cancelMedicationRequest(context, id) {
      return minsalAxios.delete(`/medication-requests/${id}`)
        .then(response => {
          return response.data;
        });
    },
    async generateMedicationRequest(context, payload) {
      return minsalAxios.post(`/medication-requests`, payload)
        .catch(error => {
          return Promise.reject(_get(error, 'response.data', { 'form': 'Ha ocurrido un error desconocido' }).reduce((errors, e) => {
            errors[e.field] = e.message;
            return errors;
          }, {}));
        });
    },
    async generateMedicationRequestForeig(context, payload){
      return minsalAxios.post(`/medication-requests-foreig`, payload)
        .catch(error => {
          return Promise.reject(_get(error, 'response.data', { 'form': 'Ha ocurrido un error desconocido' }).reduce((errors, e) => {
            errors[e.field] = e.message;
            return errors;
          }, {}));
        });
    },
    async fetchPrintedFolioRequests(context, { type, limit = 10, offset = 0, orgs = [] }) {
      return minsalAxios.get(`/printed-folio-requests?type=${type}&orgs=${orgs}&limit=${limit}&offset=${offset}`);
    },
    async printedFolioRequest(context, payload) {
      return minsalAxios.post(`/printed-folio-requests`, payload)
        .catch(e => {
          return Promise.reject({
            message: _get(e, 'response.data.error.message', 'Ha ocurrido un error al solicitar el rango de folios'),
            details: _get(e, 'response.data.error.details', []).reduce((details, fieldError) => {
              details[fieldError.field] = fieldError.message;
              return details;
            }, {})
          });
        });
    },
    async checkToken(context, payload) {
      return minsalAxios.get(`/check-token/${context.state.user.access_token}`);
    }
  },
  mutations: {
    LOGIN(state, { user, token }) {
      state.user = user;
      localStorage.setItem(`${LOCAL_STORE_PREFIX}-user-token`, token);
    },
    LOGOUT(state) {
      state.user = null;
      localStorage.removeItem(`${LOCAL_STORE_PREFIX}-user-token`);
    },
    FETCH_DRUG_FORMS_SUCCESS(state, drugForms) {
      state.drugForms = drugForms;
    },
    FETCH_UNITS_OF_TIME_SUCCESS(state, unitsOfTime) {
      state.unitsOfTime = unitsOfTime;
    },
    FETCH_ROUTE_CODES_SUCCESS(state, routeCodes) {
      state.routeCodes = routeCodes;
    }
  }
});
